import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreativeImportIssueCode,
  CreativeImportIssueDto,
  CreativeImportRowDto,
} from "@nexus/contracts";
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import { AlertCircle, AlertTriangle } from "lucide-react";

const ISSUE_CODE_ORDER: CreativeImportIssueCode[] = [
  "VENDOR_PENDING_ONBOARDING",
  "VENDOR_PRICING_MISSING",
  "SCRIPT_MISSING",
  "CLIENT_START_DATE_MISSING",
  "CLIENT_START_DATE_PREFILLED",
  "EXPECTED_DELIVERY_DATE_MISSING",
  "UNMAPPED_COLUMN",
  "DELIVERABLE_STATUS_MISMATCH",
  "VERTICAL_FORWARD_FILLED",
];

function humanizeIssueCode(code: string): string {
  return code
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function groupIssuesByCode(issues: CreativeImportIssueDto[]): CreativeImportIssueDto[][] {
  const groups = new Map<CreativeImportIssueCode, CreativeImportIssueDto[]>();

  for (const issue of issues) {
    const existing = groups.get(issue.code) ?? [];
    existing.push(issue);
    groups.set(issue.code, existing);
  }

  return [...groups.entries()]
    .sort(([codeA, issuesA], [codeB, issuesB]) => {
      if (issuesB.length !== issuesA.length) {
        return issuesB.length - issuesA.length;
      }
      return ISSUE_CODE_ORDER.indexOf(codeA) - ISSUE_CODE_ORDER.indexOf(codeB);
    })
    .map(([, groupedIssues]) => groupedIssues);
}

function sortIssuesWithinGroup(
  issues: CreativeImportIssueDto[],
  rowNumbers: Map<string, number>,
): CreativeImportIssueDto[] {
  return [...issues].sort((left, right) => {
    const leftRow = left.rowId ? rowNumbers.get(left.rowId) : null;
    const rightRow = right.rowId ? rowNumbers.get(right.rowId) : null;

    if (leftRow == null && rightRow == null) {
      return 0;
    }
    if (leftRow == null) {
      return 1;
    }
    if (rightRow == null) {
      return -1;
    }
    return leftRow - rightRow;
  });
}

function countAffectedRows(issues: CreativeImportIssueDto[]): number {
  return new Set(issues.map((issue) => issue.rowId).filter(Boolean)).size;
}

interface IssueGroupCardProps {
  issues: CreativeImportIssueDto[];
  tone: "blocking" | "warning";
  rowNumbers: Map<string, number>;
  issueTitle: (code: CreativeImportIssueCode) => string;
  issueDescription: (code: CreativeImportIssueCode) => string | null;
  rowReferenceLabel: (sheetRowNumber: number) => string;
  importWideLabel: string;
}

function IssueGroupCard({
  issues,
  tone,
  rowNumbers,
  issueTitle,
  issueDescription,
  rowReferenceLabel,
  importWideLabel,
}: IssueGroupCardProps) {
  const code = issues[0]?.code;
  if (!code) {
    return null;
  }

  const sortedIssues = sortIssuesWithinGroup(issues, rowNumbers);
  const description = issueDescription(code);
  const isBlocking = tone === "blocking";
  const Icon = isBlocking ? AlertCircle : AlertTriangle;

  return (
    <section
      className={cn(
        "overflow-hidden rounded-lg border",
        isBlocking ? "border-status-danger/30 bg-status-danger-subtle/30" : "border-status-warning/30 bg-status-warning-subtle/30",
      )}
      aria-label={issueTitle(code)}
    >
      <div className="flex items-start gap-3 border-b border-border/50 px-4 py-3">
        <Icon
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0",
            isBlocking ? "text-status-danger" : "text-status-warning",
          )}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-sm font-semibold leading-snug">{issueTitle(code)}</h3>
            <Badge variant={isBlocking ? "destructive" : "secondary"} className="shrink-0 tabular-nums">
              {issues.length}
            </Badge>
          </div>
          {description ? (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </div>
      <ul className="divide-y divide-border/50 bg-card/80">
        {sortedIssues.map((issue) => {
          const sheetRowNumber = issue.rowId ? rowNumbers.get(issue.rowId) : null;

          return (
            <li key={issue.id} className="flex items-start gap-3 px-4 py-3 text-sm">
              <span
                className={cn(
                  "mt-0.5 inline-flex min-w-[4.5rem] shrink-0 items-center justify-center rounded-md px-2 py-0.5 text-[11px] font-medium tabular-nums",
                  sheetRowNumber != null
                    ? "bg-muted text-muted-foreground"
                    : "bg-muted/60 text-muted-foreground/80",
                )}
              >
                {sheetRowNumber != null ? rowReferenceLabel(sheetRowNumber) : importWideLabel}
              </span>
              <p className="min-w-0 flex-1 leading-relaxed text-foreground">
                {issue.message ?? issueTitle(code)}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function IssueList({
  issues,
  tone,
  rows = [],
}: {
  issues: CreativeImportIssueDto[];
  tone: "blocking" | "warning";
  rows?: CreativeImportRowDto[];
}) {
  const { t } = useTranslation();

  const rowNumbers = useMemo(
    () => new Map(rows.map((row) => [row.id, row.sheetRowNumber])),
    [rows],
  );
  const groupedIssues = useMemo(() => groupIssuesByCode(issues), [issues]);

  const issueTitle = (code: CreativeImportIssueCode) =>
    t(`marketplace.admin.import.issueCode.${code}.title`, humanizeIssueCode(code));
  const issueDescription = (code: CreativeImportIssueCode) => {
    const description = t(`marketplace.admin.import.issueCode.${code}.description`, {
      defaultValue: "",
    });
    return description || null;
  };
  const rowReferenceLabel = (sheetRowNumber: number) =>
    t("marketplace.admin.import.issuesDialog.rowReference", { number: sheetRowNumber });
  const importWideLabel = t("marketplace.admin.import.issuesDialog.importWide");

  if (issues.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("marketplace.admin.import.issuesDialog.empty")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {groupedIssues.map((group) => (
        <IssueGroupCard
          key={group[0]?.code}
          issues={group}
          tone={tone}
          rowNumbers={rowNumbers}
          issueTitle={issueTitle}
          issueDescription={issueDescription}
          rowReferenceLabel={rowReferenceLabel}
          importWideLabel={importWideLabel}
        />
      ))}
    </div>
  );
}

interface ImportIssuesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tone: "blocking" | "warning";
  issues: CreativeImportIssueDto[];
  rows?: CreativeImportRowDto[];
  acknowledged?: boolean;
  onAcknowledge?: () => void;
  acknowledgePending?: boolean;
}

export function ImportIssuesDialog({
  open,
  onOpenChange,
  tone,
  issues,
  rows = [],
  acknowledged = false,
  onAcknowledge,
  acknowledgePending = false,
}: ImportIssuesDialogProps) {
  const { t } = useTranslation();
  const isBlocking = tone === "blocking";
  const affectedRowCount = countAffectedRows(issues);

  const summary =
    issues.length === 0
      ? null
      : affectedRowCount > 0
        ? t("marketplace.admin.import.issuesDialog.summary", {
            count: issues.length,
            rowCount: affectedRowCount,
          })
        : t("marketplace.admin.import.issuesDialog.summaryImportWide", { count: issues.length });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(85vh,720px)] max-w-xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-border/60 px-6 py-5">
          <div className="flex items-start justify-between gap-3 pr-6">
            <DialogTitle>
              {isBlocking
                ? t("marketplace.admin.import.blockingTitle")
                : t("marketplace.admin.import.warningsTitle")}
            </DialogTitle>
            {issues.length > 0 ? (
              <Badge variant={isBlocking ? "destructive" : "secondary"} className="shrink-0 tabular-nums">
                {issues.length}
              </Badge>
            ) : null}
          </div>
          <DialogDescription className="space-y-1">
            <span>
              {isBlocking
                ? t("marketplace.admin.import.blockingHint")
                : acknowledged
                  ? t("marketplace.admin.import.warningsAcknowledgedHint")
                  : t("marketplace.admin.import.warningsHint")}
            </span>
            {summary ? <span className="block text-foreground/80">{summary}</span> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <IssueList issues={issues} tone={tone} rows={rows} />
        </div>
        <DialogFooter className="shrink-0 gap-2 border-t border-border/60 px-6 py-4 sm:justify-between">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.close", "Close")}
          </Button>
          {!isBlocking && onAcknowledge && !acknowledged ? (
            <Button type="button" disabled={acknowledgePending} onClick={onAcknowledge}>
              {acknowledgePending
                ? t("marketplace.admin.import.quickActions.acceptingWarnings")
                : t("marketplace.admin.import.summary.nextStep.warnings.acknowledgeAction")}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
